import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Grid, Stack, Chip, Divider,
} from '@mui/material';
import {
  Bolt, Check, Remove,
} from '@mui/icons-material';
import { C, Section, SectionLabel } from './theme';

/*
  Pricing logic
  --------------
  Anchor: Card 2 ("Growth") = ₹32,000 / year  ->  32000 / 12 = ₹2,667 / mo
  All other cards are calculated proportionally from that anchor so the
  relative value of every tier stays consistent:
    Card 1 (Starter)     = 0.5x anchor  -> ₹1,333/mo
    Card 2 (Growth)       = 1x anchor    -> ₹2,667/mo   (₹32,000/yr)
    Card 3 (Business)     = 2x anchor    -> ₹5,333/mo
    Card 4 (Cloud Scale)  = 4x anchor    -> ₹10,667/mo
  "Regular" (strike-through) price is derived from each card's own discount %,
  and the 48-month totals are simply price/mo * 48.
*/
const plans = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Run a simple site smoothly. Great for personal pages.',
    off: '75% off',
    recommended: false,
    price: 1333,
    regular: 5332,
    months: 48,
    totalDiscounted: 63984,
    totalRegular: 255936,
    features: [
      { text: '1 website', included: true },
      { text: 'Domain – free for 1 year', included: true },
      { text: '10 GB SSD storage', included: true },
      { text: 'Free weekly backups', included: true },
      { text: 'CDN', included: false },
      { text: 'Integrated ecommerce', included: false },
      { text: 'AI tools', included: false },
      { text: 'Priority 24/7 expert support', included: false },
    ],
    buildWith: [
      { text: 'Vibe coding (3 credits)', included: true },
      { text: 'Drag-and-drop website builder', included: true },
      { text: 'WordPress', included: true },
      { text: 'Node.js', included: false },
    ],
    grow: [
      { text: '1 mailbox – free for 1 year', included: true },
      { text: 'AI email marketing', included: false },
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'Unlimited websites and mailboxes, plus AI tools and priority support.',
    off: 'Special offer • 80% off',
    recommended: true,
    price: 2667,
    regular: 13335,
    months: 48,
    totalDiscounted: 128016,
    totalRegular: 640080,
    features: [
      { text: 'Unlimited websites', included: true },
      { text: 'Domain – free for 1 year', included: true },
      { text: '50 GB NVMe storage', included: true },
      { text: 'Daily backups + easy data restore', included: true },
      { text: 'CDN included', included: true },
      { text: 'Integrated ecommerce', included: true },
      { text: 'AI tools', included: true },
      { text: 'Priority 24/7 expert support', included: true },
    ],
    buildWith: [
      { text: 'Vibe coding (15 credits)', included: true },
      { text: 'Drag-and-drop website builder', included: true },
      { text: 'WordPress', included: true },
      { text: 'Node.js', included: true },
    ],
    grow: [
      { text: 'Unlimited mailboxes – free for 1 year', included: true },
      { text: 'AI email marketing – free for 1 year', included: true },
    ],
    why: 'A complete solution for long-term projects. Everything included.',
  },
  {
    id: 'business',
    name: 'Business',
    tagline: 'More power and storage for growing traffic and teams.',
    off: '70% off',
    recommended: false,
    price: 5333,
    regular: 17777,
    months: 48,
    totalDiscounted: 255984,
    totalRegular: 853296,
    features: [
      { text: 'Unlimited websites', included: true },
      { text: 'Domain – free for 1 year', included: true },
      { text: '100 GB NVMe storage', included: true },
      { text: 'Daily and on-demand backups', included: true },
      { text: 'CDN included', included: true },
      { text: 'Integrated ecommerce', included: true },
      { text: 'AI tools', included: true },
      { text: 'Priority 24/7 expert support', included: true },
    ],
    buildWith: [
      { text: 'Vibe coding (25 credits)', included: true },
      { text: 'Drag-and-drop website builder', included: true },
      { text: 'WordPress', included: true },
      { text: 'Node.js', included: true },
    ],
    grow: [
      { text: 'Unlimited mailboxes – free for 1 year', included: true },
      { text: 'AI email marketing – free for 1 year', included: true },
    ],
  },
  {
    id: 'cloud-scale',
    name: 'Cloud Scale',
    tagline: 'Dedicated power for agencies or high-traffic projects.',
    off: '65% off',
    recommended: false,
    price: 10667,
    regular: 30477,
    months: 48,
    totalDiscounted: 512016,
    totalRegular: 1462896,
    features: [
      { text: 'Unlimited websites', included: true },
      { text: 'Domain – free for 1 year', included: true },
      { text: '250 GB NVMe storage', included: true },
      { text: 'Daily, on-demand & geo-redundant backups', included: true },
      { text: 'CDN included', included: true },
      { text: 'Integrated ecommerce', included: true },
      { text: 'AI tools', included: true },
      { text: 'Priority 24/7 expert support', included: true },
    ],
    buildWith: [
      { text: 'Vibe coding (40 credits)', included: true },
      { text: 'Drag-and-drop website builder', included: true },
      { text: 'WordPress', included: true },
      { text: 'Node.js', included: true },
    ],
    grow: [
      { text: 'Unlimited mailboxes – free for 1 year', included: true },
      { text: 'AI email marketing – free for 1 year', included: true },
    ],
  },
];

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

const FeatureRow = ({ text, included }) => (
  <Stack direction="row" spacing={1.3} alignItems="flex-start">
    <Box sx={{ mt: '2px', color: included ? C.success : '#D1D5DB', display: 'flex' }}>
      {included ? <Check sx={{ fontSize: 17 }} /> : <Remove sx={{ fontSize: 17 }} />}
    </Box>
    <Typography sx={{
      fontSize: '0.86rem',
      color: included ? C.textSecondary : '#9CA3AF',
      lineHeight: 1.4,
    }}>
      {text}
    </Typography>
  </Stack>
);

const WebsitePricing = () => {
  const navigate = useNavigate();

  const handleSelectPlan = (plan) => {
    const serializablePlan = {
      id: plan.id,
      name: `${plan.name} Website Hosting`,
      badge: plan.recommended ? 'RECOMMENDED' : null,
      desc: plan.tagline,
      price: inr(plan.price),
      save: plan.off,
      term: `Get ${plan.months} months for ${inr(plan.totalDiscounted)} (regular price ${inr(plan.totalRegular)})`,
      renew: `Renews at ${inr(plan.regular)}/mo`,
      specsText: plan.features.filter((f) => f.included).map((f) => f.text).join(' · '),
      features: plan.features.filter((f) => f.included).map((f) => f.text),
      category: 'Website Hosting',
    };
    navigate('/checkout', { state: { plan: serializablePlan } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Section id="website-pricing" sx={{ background: '#FFFFFF' }}>
      <Container maxWidth="lg">

        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Website Hosting</SectionLabel>
          <Typography variant="h2" sx={{
            fontWeight: 800, fontSize: { xs: '1.85rem', md: '2.5rem' },
            color: C.textPrimary, letterSpacing: '-0.03em', mb: 1.5,
          }}>
            Choose Your Website Hosting Plan
          </Typography>
          <Typography sx={{ color: C.textSecondary, maxWidth: 620, mx: 'auto', fontSize: '1.02rem', lineHeight: 1.6 }}>
            Fast, reliable hosting with a free domain, email, and easy-to-use
            website building tools on every plan.
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={3} alignItems="stretch">
          {plans.map((plan) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={plan.id}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex', flexDirection: 'column',
                  borderRadius: '16px',
                  border: plan.recommended ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                  background: plan.recommended ? '#FAFBFD' : '#FFFFFF',
                  boxShadow: plan.recommended
                    ? '0 12px 30px rgba(37,99,235,0.12)'
                    : '0 2px 10px rgba(0,0,0,0.03)',
                  position: 'relative',
                  overflow: 'visible',
                  p: 3,
                }}
              >
                {/* Recommended badge */}
                {plan.recommended && (
                  <Box sx={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                    background: C.primary, color: '#FFFFFF', fontWeight: 800,
                    fontSize: '0.7rem', letterSpacing: '0.1em',
                    px: 2.5, py: 0.6, borderRadius: '20px', whiteSpace: 'nowrap',
                  }}>
                    RECOMMENDED
                  </Box>
                )}

                {/* Plan name */}
                <Typography sx={{
                  fontWeight: 800, fontSize: '1.15rem', color: C.textPrimary, mb: 0.8, mt: 0.5,
                }}>
                  {plan.name}
                </Typography>
                <Typography sx={{
                  fontSize: '0.82rem', lineHeight: 1.5, mb: 2.5, minHeight: 42, color: C.textMuted,
                }}>
                  {plan.tagline}
                </Typography>

                {/* Price block */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{
                    fontSize: '0.85rem', textDecoration: 'line-through', color: '#9CA3AF',
                  }}>
                    {inr(plan.regular)}
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={0.5}>
                    <Typography sx={{
                      fontWeight: 900, fontSize: '2.2rem', lineHeight: 1, color: C.textPrimary,
                    }}>
                      {inr(plan.price)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: C.textMuted }}>
                      /mo
                    </Typography>
                  </Stack>
                  <Chip label={plan.off} size="small" sx={{
                    mt: 1, background: C.successLight, color: C.success,
                    fontWeight: 700, fontSize: '0.72rem', border: `1px solid ${C.success}40`,
                  }} />
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSelectPlan(plan)}
                  sx={{
                    mb: 1.5, py: 1.3, borderRadius: '8px', textTransform: 'none',
                    fontWeight: 700, fontSize: '0.9rem', boxShadow: 'none',
                    background: plan.recommended ? C.primary : '#1E293B',
                    color: '#FFFFFF',
                    '&:hover': {
                      background: plan.recommended ? C.primaryHover : '#0F172A',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Choose plan
                </Button>

                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.5, mb: 2, color: C.textMuted }}>
                  Get {plan.months} months for {inr(plan.totalDiscounted)} (regular price {inr(plan.totalRegular)}).
                  {' '}Renews at {inr(plan.regular)}/mo.
                </Typography>

                <Divider sx={{ mb: 2, borderColor: C.border }} />

                {/* Features */}
                <Stack spacing={1.1} sx={{ mb: 2.5 }}>
                  {plan.features.map((f) => (
                    <FeatureRow key={f.text} text={f.text} included={f.included} />
                  ))}
                </Stack>

                {/* Build with */}
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 1, color: C.textMuted }}>
                  Build with:
                </Typography>
                <Stack spacing={1.1} sx={{ mb: 2.5 }}>
                  {plan.buildWith.map((f) => (
                    <FeatureRow key={f.text} text={f.text} included={f.included} />
                  ))}
                </Stack>

                {/* Communicate & grow */}
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 1, color: C.textMuted }}>
                  Communicate & grow:
                </Typography>
                <Stack spacing={1.1} sx={{ mb: plan.why ? 2.5 : 0 }}>
                  {plan.grow.map((f) => (
                    <FeatureRow key={f.text} text={f.text} included={f.included} />
                  ))}
                </Stack>

                {/* Why this plan box */}
                {plan.why && (
                  <Box sx={{
                    mt: 'auto', background: C.primaryLight, borderRadius: '10px',
                    border: `1px solid ${C.primary}25`, p: 2,
                  }}>
                    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.5 }}>
                      <Bolt sx={{ fontSize: 16, color: C.primary }} />
                      <Typography sx={{ color: C.primary, fontWeight: 700, fontSize: '0.8rem' }}>
                        Why this plan?
                      </Typography>
                    </Stack>
                    <Typography sx={{ color: C.textSecondary, fontSize: '0.78rem', lineHeight: 1.5 }}>
                      {plan.why}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

      </Container>
    </Section>
  );
};

export default WebsitePricing;
